/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useMemo } from "react";
import type { ReactNode } from "react";
import { useMsal } from "@azure/msal-react";
import { extractRoles } from "./AuthProvider";
import { isAuthDisabled } from "./bootstrapConfig";

interface RbacSection {
  label: string;
  roles: string[];
  routes: string[];
}

interface RbacConfig {
  bypass_role: string;
  universal_routes: string[];
  sections: Record<string, RbacSection>;
}

interface RbacContextValue {
  userRoles: string[];
  sections: Record<string, RbacSection>;
  hasAccess: (path: string) => boolean;
  visibleSections: Record<string, RbacSection>;
}

const RbacCtx = createContext<RbacContextValue>({
  userRoles: [],
  sections: {},
  hasAccess: () => true,
  visibleSections: {},
});

export function useRbac(): RbacContextValue {
  return useContext(RbacCtx);
}

interface Props {
  rbacConfig: RbacConfig | null;
  children: ReactNode;
}

export function RbacProvider({ rbacConfig, children }: Props) {
  const { instance } = useMsal();

  const userRoles = useMemo(() => {
    if (isAuthDisabled()) {
      return ["Quill.Admin", "Quill.QE", "Quill.Finance", "Quill.Superuser"];
    }
    const account = instance.getActiveAccount();
    return extractRoles(account?.idTokenClaims);
  }, [instance]);

  const value = useMemo<RbacContextValue>(() => {
    if (!rbacConfig) {
      return { userRoles, sections: {}, hasAccess: () => true, visibleSections: {} };
    }

    const isSuperuser = rbacConfig.bypass_role && userRoles.includes(rbacConfig.bypass_role);
    const roleSet = new Set(userRoles);

    const hasAccess = (path: string): boolean => {
      if (isSuperuser) return true;
      if (rbacConfig.universal_routes.includes(path)) return true;

      for (const section of Object.values(rbacConfig.sections)) {
        const matchesRoute = section.routes.some((route) => {
          if (route.includes(":")) {
            const pattern = new RegExp("^" + route.replace(/:[^/]+/g, "[^/]+") + "$");
            return pattern.test(path);
          }
          return route === path;
        });
        if (matchesRoute) {
          return section.roles.some((r) => roleSet.has(r));
        }
      }

      return true;
    };

    const visibleSections: Record<string, RbacSection> = {};
    for (const [key, section] of Object.entries(rbacConfig.sections)) {
      if (isSuperuser || section.roles.some((r) => roleSet.has(r))) {
        visibleSections[key] = section;
      }
    }

    return { userRoles, sections: rbacConfig.sections, hasAccess, visibleSections };
  }, [rbacConfig, userRoles]);

  return <RbacCtx.Provider value={value}>{children}</RbacCtx.Provider>;
}
